import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { api, setSessionLostHandler, type AdminProfile } from './api';
import { applyStoredTheme } from './lib/theme';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { LoginPage } from './pages/LoginPage';
import { MfaPage } from './pages/MfaPage';

type View =
  | { name: 'loading' }
  | { name: 'login' }
  | { name: 'mfa' }
  | { name: 'password' }
  | { name: 'dashboard'; admin: AdminProfile };

function viewFor(admin: AdminProfile): View {
  if (!admin.mfaVerified) return { name: 'mfa' };
  if (admin.mustChangePassword) return { name: 'password' };
  return { name: 'dashboard', admin };
}

// The console (router, every page) loads only after sign-in; the login screens stay light.
const ConsoleApp = lazy(() =>
  import('./console/ConsoleApp').then((module) => ({ default: module.ConsoleApp })),
);

// The embedded inbox has its own token and never touches the admin session.
const EmbedApp = lazy(() =>
  import('./embed/EmbedApp').then((module) => ({ default: module.EmbedApp })),
);
const onEmbedPage = (): boolean => window.location.pathname.toLowerCase().startsWith('/embed');

// The console and its sign-in screens wear the saved theme from the first paint.
if (!onEmbedPage()) applyStoredTheme();

export function App() {
  return onEmbedPage() ? (
    <Suspense fallback={<div className="min-h-svh" aria-busy="true" />}>
      <EmbedApp />
    </Suspense>
  ) : (
    <AdminApp />
  );
}

function AdminApp() {
  const [view, setView] = useState<View>({ name: 'loading' });

  const refresh = useCallback(async () => {
    try {
      setView(viewFor(await api.me()));
    } catch {
      setView({ name: 'login' });
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setView({ name: 'login' });
  }, []);

  useEffect(() => {
    void refresh();
    setSessionLostHandler(() => void refresh());
    return () => setSessionLostHandler(null);
  }, [refresh]);

  switch (view.name) {
    case 'loading':
      return <div className="min-h-svh" aria-busy="true" />;
    case 'login':
      return <LoginPage onSuccess={(step) => (step ? setView({ name: 'mfa' }) : void refresh())} />;
    case 'mfa':
      return <MfaPage onSuccess={refresh} onCancel={signOut} />;
    case 'password':
      return <ChangePasswordPage onChanged={refresh} onCancel={signOut} />;
    case 'dashboard':
      return (
        <Suspense fallback={<div className="min-h-svh" aria-busy="true" />}>
          <ConsoleApp admin={view.admin} onSignedOut={() => setView({ name: 'login' })} />
        </Suspense>
      );
  }
}
